/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { Fragment } from 'react';
import { RouteComponent } from 'react-resource-router';
import { Panel, PanelSplitter, Aside } from '@atlaskit/navigation-system';
import { usePanelController } from '../../contexts/PanelContext';
import { cssMap, jsx } from '@compiled/react';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useLeftSidebarController } from '../../contexts/LeftSidebarContext';
import { useAsideController } from '../../contexts/AsideContext';

const styles = cssMap({
	mainRow: {
		display: 'flex',
		alignItems: 'stretch',
		height: '100%',
		minHeight: '0px',
	},
	leftColumn: {
		flexShrink: 0,
		minHeight: '100vh',
		paddingInlineStart: token('space.300'),
		paddingInlineEnd: token('space.300'),
	},
	mainColumn: {
		flexGrow: 1,
		minWidth: '0px',
		height: '100%',
		minHeight: '0px',
	},
	resizeHandle: {
		width: '4px',
		cursor: 'col-resize',
		alignSelf: 'stretch',
		backgroundColor: token('elevation.surface.sunken'),
	},
});

type RightSidePanelProps = {
	testId?: string;
};

export const RightSidePanel = ({
	testId = 'nav-right-side-panel',
}: RightSidePanelProps): JSX.Element | null => {
	const { isOpen, content } = usePanelController();
	if (!isOpen) {
		return null;
	}
	return (
		<Panel defaultWidth={600} testId={testId}>
			<PanelSplitter label="Resize panel" />
			{content}
		</Panel>
	);
};

type RightAsidePanelProps = {
	testId?: string;
};

export const RightAsidePanel = ({
	testId = 'nav-right-aside-panel',
}: RightAsidePanelProps): JSX.Element | null => {
	const { isOpen, content } = useAsideController();
	if (!isOpen || !content) {
		return null;
	}
	return (
		<Aside defaultWidth={500} testId={testId}>
			<PanelSplitter label="Resize chat" />
			{content}
		</Aside>
	);
};

type LeftSidePanelProps = {
	testId?: string;
};

export const LeftSidePanel = ({
	testId = 'nav-left-side-panel',
}: LeftSidePanelProps): JSX.Element | null => {
	const { content } = useLeftSidebarController();
	const [sidebarWidth, setSidebarWidth] = React.useState(460);

	if (!content) {
		return null;
	}

	// temporary hack until I redo this with a proper ADS sidebar.
	const handleResizeMouseDown = (e: React.MouseEvent) => {
		const startX = e.clientX;
		const startWidth = sidebarWidth;

		const handleMouseMove = (moveEvent: MouseEvent) => {
			const delta = moveEvent.clientX - startX;
			const nextWidth = Math.min(640, Math.max(420, startWidth + delta));
			setSidebarWidth(nextWidth);
		};

		const handleMouseUp = () => {
			window.removeEventListener('mousemove', handleMouseMove);
			window.removeEventListener('mouseup', handleMouseUp);
		};

		window.addEventListener('mousemove', handleMouseMove);
		window.addEventListener('mouseup', handleMouseUp);
	};

	return (
		<Fragment>
			<Box xcss={styles.leftColumn} style={{ width: sidebarWidth }} testId={testId}>
				{content}
			</Box>
			<Box xcss={styles.resizeHandle} onMouseDown={handleResizeMouseDown} />
		</Fragment>
	);
};

type MainWithLeftSidebarProps = {
	testId?: string;
};

export const MainWithLeftSidebar = ({
	testId = 'nav-main-with-left-sidebar',
}: MainWithLeftSidebarProps): JSX.Element | null => {
	return (
		<Box xcss={styles.mainRow} testId={testId}>
			<LeftSidePanel />
			<Box xcss={styles.mainColumn}>
				<RouteComponent />
			</Box>
		</Box>
	);
};
